// Actions are all about taking an app situation (some sort of pseudo event like 'bootingStarted' or 'mapClicked') as an action and committing to the app's state.
// Ideally actions should be short and sweet and we should avoid as much logic as possible.
// Avoid dispatching other actions from within actions (nesting actions).
// More complex actions should be moved into a separate handler file.
// Shared behaviour can be abstracted into a helper function.

import { openPaneComponent } from "./actions/helpers/pane_helpers";
import {
  getCurrentPaneOptionComponent,
  selectPaneOption,
} from "./actions/helpers/pane_option_helpers";
import { setBooting } from "../../../online_app/instances/state_machine/actions/helpers/commit_helpers";

import { initFromPersistentStore } from "../../../../lib/mixins/persistable";
import { getLastPaneOptionFromOpenedMapFilterCodes } from "./actions/helpers/map_filter_helpers";
import { recordPaneOptionsComponentOpenState } from "./actions/helpers/commit_helpers";

export default {
  bootingStarted(sm) {
    setBooting(sm, true);
  },

  bootingEnded(sm) {
    setBooting(sm, false);
  },

  initFromPersistentStore,

  openPane(sm, pane) {
    openPaneComponent(sm, pane);
  },

  // This action is called when the URL changes with a route request. This normally happens when:
  // - a user navigates to a URL directly or uses the back/forward commands (all handled by the OnlineApp JS).
  // These will dispatch a `OnlineAppNavOptionConfig.selectedWithRouteRequestEventName` event which is picked up by the `PaneOptionComponent`.
  // The `PaneOptionComponent` then calls this action.
  selectPaneOptionComponentWithRouteRequest(sm, paneOptionComponent) {
    this.paneOptionSelect(sm, paneOptionComponent);
  },

  viewPaneOptionComponent(sm, paneOptionComponent) {
    paneOptionComponent.trackViewed();
  },

  /**
   * This action might be dispatched when a user clicks on a pane option sub-component or when the URL changes with a route request.
   *
   * @param sm
   * @param paneOptionComponent
   */
  paneOptionSelect(sm, paneOptionComponent) {
    const nav = sm.getStateableObject(); // Get a reference to the `Nav` object via the state machine.

    // Communicate only with newer (from Oct 2024) native apps via a signal as to whether the nav should be toggled open or closed when the pane option is clicked.
    if (nav) {
      const navToggleInstruction =
        paneOptionComponent.getNavToggleInstruction();
      if (navToggleInstruction === "open") nav.openNav();
      if (navToggleInstruction === "close") nav.closeNav();
    }

    selectPaneOption(sm, paneOptionComponent);

    if (paneOptionComponent.shouldOpenPane()) {
      // TODO - Eventually we can remove this deprecated recording of showing content. This is only for older native apps up until Oct 2024.
      // Communicate only with older native apps via `data-nav_is_showing_content` data attribute as to whether the pane option is showing an associated content pane.
      // The native apps decide whether to keep the nav open or not based on this attribute. However it's not a great solution as sometimes the nav should be open even if the pane is not showing content.
      paneOptionComponent.recordShowingContent();

      openPaneComponent(sm, paneOptionComponent.getAssociatedPane());
    } else {
      // If the nav pane is not showing content then we want to close the pane.
      if (!paneOptionComponent.isNavPaneShowingContent()) {
        // We should close any existing pane (eg. open the primary pane). Otherwise we can end up with a map showing content unrelated to the open pane.
        const primaryPane = nav?.panesManager?.defaultPane;
        if (primaryPane) openPaneComponent(sm, primaryPane);
      }
    }
  },

  /**
   * Pane Link Click Event Process:
   * For clicks on the main link of a pane option we call the `paneOptionSelect` action to do all the standard work.
   *
   * @param sm
   * @param paneLinkComponent
   */
  paneLinkClick(sm, paneLinkComponent) {
    const paneOptionComponent = paneLinkComponent.paneOptionComponent;
    if (!paneOptionComponent) return;

    this.paneOptionSelect(sm, paneOptionComponent);
  },

  /**
   * Pane Info Click Event Process:
   * For clicks on the info icon of a pane option we want to always show the info pane, so we set the `willShowPaneOverride` to `true`.
   * With that configuration done we can simply we call the `paneLinkClick` action to do all the standard work.
   *
   * @param sm
   * @param paneInfoComponent
   */
  paneInfoClick(sm, paneInfoComponent) {
    const paneOptionComponent = paneInfoComponent.paneOptionComponent;
    if (!paneOptionComponent) return;

    paneOptionComponent.willShowPaneOverride = true; // Because this is an 'info' click we will always show the pane.
    this.paneOptionSelect(sm, paneOptionComponent);
    paneOptionComponent.willShowPaneOverride = null; // Reset the flag.
  },

  closePaneClick(sm, closePaneComponent) {
    openPaneComponent(
      sm,
      closePaneComponent.pane.getPanesManager().defaultPane,
    ); // Open the primary pane (which closes the current one).

    // If the current pane option (the one that opened the pane we're closing) has no map filter then we're going to be left with a blank map without markers (bummer!).
    // So let's automatically "click" the most recent pane option that does have a map filter.
    // Because it's a "click" (rather than just changing state) the `Nav` and the `OnlineApp` (and native apps) will all respond appropriately.
    // Clicking the most recent pane option that has a map filter will therefore mean that it is also the "next" pane option.
    const currentPaneOptionComponent = getCurrentPaneOptionComponent(sm);
    if (!currentPaneOptionComponent.hasMapFilter) {
      const nextPaneOptionComponent =
        getLastPaneOptionFromOpenedMapFilterCodes(sm);
      if (nextPaneOptionComponent && nextPaneOptionComponent.hasMapFilter) {
        // We configure the next pane option to behave as we want it to - to open the nav and not show its associated pane.
        // Note that this config could be sent as a detail payload in the event that the next pane option listens for.
        // However we use the `click()` function to fire the event (rather than a custom event) so we can't send a payload.
        nextPaneOptionComponent.willShowPaneOverride = false; // We're using this to force the next pane option to *not* show its associated pane regardless of its standard behaviour.
        nextPaneOptionComponent.navToggleInstructionOverride = "open"; // We're using this to force the next pane option to *open* the nav regardless of its standard behaviour.
        nextPaneOptionComponent.fireClickEventOnLink({
          willShowPaneOverride: false,
          navToggleInstructionOverride: "open",
        }); // Fire the click event on the next pane option and pass the config as a payload.
        nextPaneOptionComponent.navToggleInstructionOverride = null; // Reset the the flag.
        nextPaneOptionComponent.willShowPaneOverride = null; // Reset the the flag to ensure the next pane option behaves as normal.
      }
    }
  },

  paneOptionsComponentOpen(sm, paneOptionsComponent) {
    recordPaneOptionsComponentOpenState(sm, {
      paneOptionsComponent: paneOptionsComponent,
      open: true,
    });
  },

  paneOptionsComponentClose(sm, paneOptionsComponent) {
    recordPaneOptionsComponentOpenState(sm, {
      paneOptionsComponent: paneOptionsComponent,
      open: false,
    });
  },
};
